/* EditorGroupsRenderer.js */

"use strict";
const {keys, values, isEmpty, debounce, inRange} = require("lodash");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const BaseRefResolver = Forms.resolvers.ref.base;

class ExpandCollapseRenderer {
    constructor(model, entitySchemaObject) {
        this._model = model;
        this._entitySchema = entitySchemaObject;
        this._rootElem = document.createElement("editor-groups-gl");
        this._rootElem.component = this._model;

        this._floatingNavigation = document.createElement("div");
        this._floatingNavigation.classList.add("floating-editor-navigation");

        this._observer = {};
        this._navItemsObserver = {};

        this._jtype = {};
        this._navItem = [];
        this._maxHeight = 0;

        if (!isEmpty(this._model._nibApiService)) {
            this._refResolver = new BaseRefResolver(
                this._model._nibApiService,
                this._model._userSession,
                true
            );
            this._formsComponent = new FormsComponent(this._refResolver, undefined, false);
        } else {
            this._formsComponent = new FormsComponent();
        }

        this._parentViewScroll = this._parentViewScroll.bind(this);
        this._parentViewScrollEnd = this._parentViewScrollEnd.bind(this);
        this._isScrolling = false;

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._registerFormsComponentListeners();

        // Event to check resize
        this._observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            const height = entry.contentRect.height;
            this._setHeightToFloatingDiv(height);
        });

        this._observer.observe(this._rootElem);

        this._model.on(
            "SetFormPristineValue",
            (this._setFormPristineValue = this._setFormPristineValue.bind(this))
        );

        this._model.on(
            "SetEntitySchemaObject",
            (this._setEntitySchemaObject = this._setEntitySchemaObject.bind(this))
        );
    }

    _registerFormsComponentListeners() {
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = debounce(
                this._onFormsPendingPatchesChanged.bind(this),
                500,
                false
            ))
        );
    }

    _unregisterListeners() {
        this._unregisterFormsComponentListeners();

        this._observer.disconnect();

        // Adding check so that this doesn't fail
        if (this._navItemsObserver.disconnect) {
            this._navItemsObserver.disconnect();
        }

        this._model.removeListener("SetFormPristineValue", this._setFormPristineValue);
    }

    _unregisterFormsComponentListeners() {
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
    }

    _unregisterScrollEventsOnView() {
        const parentViewEl =
            typeof this._model._parentViewElement === "string"
                ? document.querySelector(this._model._parentViewElement)
                : this._model._parentViewElement;
        if (parentViewEl) {
            parentViewEl.removeEventListener("scroll", this._parentViewScroll);
            parentViewEl.removeEventListener("scrollend", this._parentViewScrollEnd);
        }
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._onFormsPendingPatchesChanged.flush();
        let promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(() => {
            //TODO set all class members to undefined here
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._navItem = undefined;
            this._entitySchema = undefined;
            this._floatingNavigation = undefined;
            this._maxHeight = undefined;
            this._jtype = undefined;
        });
    }

    async shutdownAndRecreateFormComponent() {
        this._unregisterFormsComponentListeners();
        await this._formsComponent.shutdown();
        this._formsComponent = {};
        if (!isEmpty(this._model._nibApiService)) {
            this._refResolver = new BaseRefResolver(
                this._model._nibApiService,
                this._model._userSession,
                true
            );
            this._formsComponent = new FormsComponent(this._refResolver, undefined, false);
        } else {
            this._formsComponent = new FormsComponent();
        }
        this._registerFormsComponentListeners();
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    async render() {
        if (this._rootElem.hasChildNodes()) {
            if (this._formsComponent.shutdown) {
                await this.shutdownAndRecreateFormComponent();
            }
            this._rootElem.innerHTML = "";
        }
        if (!this._rootElem.hasChildNodes()) {
            await this._createEditorSections();
        }
        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    get formsComponent() {
        return this.getFormsComponent();
    }

    getFormsComponent() {
        return this._formsComponent;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    async _createEditorSections() {
        try {
            if (this._entitySchema) {
                this._maxHeight = 0;
                const sections = keys(this._entitySchema);
                this._navItem = [];
                this._floatingNavigation.innerHTML = "";
                this._rootElem.appendChild(this._floatingNavigation);
                this._rootElem.appendChild(this._formsComponent._renderer.getRootElement());
                let firstItemSelected = false;
                sections.forEach((section, idx) => {
                    const currentSection = this._entitySchema[section];
                    const currentSectionElem = document.createElement("section");
                    const sectionTitle = document.createElement("section-title");
                    sectionTitle.innerText = currentSection.label;

                    currentSectionElem.appendChild(sectionTitle);
                    this._createJtype(section, this._entitySchema[section]);

                    if (!isEmpty(currentSection.label)) {
                        this._maxHeight += 50;
                        this._navItem[idx] = document.createElement("div");
                        this._navItem[idx].classList.add("editor-nav-item");
                        if (!firstItemSelected) {
                            this._navItem[idx].classList.add("active-nav");
                            firstItemSelected = true;
                        }
                        this._navItem[idx].setAttribute("name", section);
                        this._navItem[idx].setAttribute("data-value", currentSection.label);
                        this._navItem[idx].innerText = currentSection.label;

                        this._navItem[idx].addEventListener("click", (e) => {
                            this._registerScrollEventsOnView();
                            this._navItem.forEach((navItem) =>
                                navItem.classList.remove("active-nav")
                            );
                            e.target.classList.add("active-nav");
                            const anchorElem = this._rootElem.querySelector(
                                `.legend[title='${currentSection.label}']`
                            );

                            // Calculate the top offset
                            const topOffset = anchorElem.offsetTop - 10; // 10rem = 160px

                            // Scroll to the anchor element with offset
                            if (this._rootElem.parentElement) {
                                this._rootElem.parentElement.scrollTo({
                                    top: topOffset,
                                    behavior: "smooth",
                                });
                            } else {
                                this.anchorElem.scrollIntoView({behavior: "smooth"});
                            }

                            // If user clicks on the first nav item, then we scroll to top
                            if (this._floatingNavigation.children[0] === e.target) {
                                if (this._rootElem.parentElement) {
                                    this._rootElem.parentElement.scrollTo({
                                        top: 0,
                                        behavior: "smooth",
                                    });
                                }
                            }
                        });

                        this._floatingNavigation.appendChild(this._navItem[idx]);
                    }
                });

                this._formsComponent.setTypeInformation(this._jtype, true);

                if (!isEmpty(this._model._pristineValue)) {
                    this._formsComponent.setPristineValue(this._model._pristineValue, true);
                }

                await this._formsComponent.renderer.render();

                this._registerLegendScrollEvent();
            }

            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    _registerLegendScrollEvent() {
        const scrollTargets = this._rootElem.querySelectorAll(".form_depth_0.rendered_control");

        this._navItemsObserver = new IntersectionObserver(
            (entries) => {
                // Skip if scrolling
                if (this._isScrolling) {
                    return;
                }

                // Skip if this._rootElem.parentElement is undefined
                const parent = this._rootElem.parentElement;
                if (!parent) {
                    return;
                }

                const windowTop = parent.getBoundingClientRect().top;

                if (
                    this._floatingNavigation.style.left !==
                    `${this._rootElem.getBoundingClientRect().left}px`
                ) {
                    this._floatingNavigation.style.left = `${
                        this._rootElem.getBoundingClientRect().left
                    }px`;
                }

                let topMostLegend = null;

                // Find the topmost legend within the range
                entries.forEach((entry) => {
                    const topPosition = entry.boundingClientRect.top;
                    const bottomPosition = entry.boundingClientRect.bottom;
                    if (inRange(topPosition + 100, windowTop, windowTop + 150)) {
                        topMostLegend = entry.target;
                    }
                    if (!topMostLegend) {
                        if (bottomPosition < windowTop + 70) {
                            topMostLegend = entry.target.nextElementSibling;
                        }
                    }
                });

                if (topMostLegend) {
                    this._navItem.forEach((navItem) => {
                        navItem.classList.remove("active-nav");
                    });
                    const legendEl = topMostLegend.querySelector(".legend");
                    const currentItem = this._floatingNavigation.querySelector(
                        `.editor-nav-item[data-value='${
                            legendEl ? legendEl.innerText : "General"
                        }']`
                    );
                    if (currentItem) {
                        currentItem.classList.add("active-nav");
                        const navRect = this._floatingNavigation.getBoundingClientRect();
                        const itemRect = currentItem.getBoundingClientRect();
                        const isOutOfView =
                            itemRect.top < navRect.top || // Above the visible area
                            itemRect.bottom > navRect.bottom; // Below the visible area

                        if (isOutOfView) {
                            currentItem.scrollIntoView({
                                behavior: "smooth",
                                block: "nearest", // Scroll only enough to make it visible
                            });
                        }
                    }
                }
            },
            {
                root: null,
                rootMargin: "250px 0px -110px 0px",
                threshold: [0, 0.25, 0.5, 0.75, 1],
            }
        );

        scrollTargets.forEach((element) => {
            this._navItemsObserver.observe(element);
        });
    }
    _registerScrollEventsOnView() {
        const parentViewEl =
            typeof this._model._parentViewElement === "string"
                ? document.querySelector(this._model._parentViewElement)
                : this._model._parentViewElement;
        if (parentViewEl) {
            parentViewEl.addEventListener("scroll", this._parentViewScroll);
            parentViewEl.addEventListener("scrollend", this._parentViewScrollEnd);
        }
    }

    _parentViewScroll() {
        this._isScrolling = true;
    }

    _parentViewScrollEnd() {
        this._isScrolling = false;
        this._unregisterScrollEventsOnView();
    }

    _createJtype(section, sectionObject) {
        this._jtype[section] = {};
        this._jtype[`${section}@Display.label`] = sectionObject.label;

        keys(sectionObject.props).forEach((property) => {
            const currentProperty = sectionObject.props[property];

            // Datatypes
            if (currentProperty.dataType === "text") {
                this._jtype[section][`${property}@Display.label`] = currentProperty.label;
                this._jtype[section][`${property}@Display.editor`] = {
                    minLineCount: 4,
                    maxLineCount: 4,
                };
            } else if (
                currentProperty.dataType === "string" ||
                currentProperty.dataType === "number" ||
                currentProperty.dataType === "boolean" ||
                currentProperty.dataType === "dateTime"
            ) {
                this._jtype[section][`${property}@Display.label`] = currentProperty.label;
            } else if (currentProperty.dataType === "enum") {
                this._jtype[section][`${property}@Display.label`] = currentProperty.label;
                this._jtype[section][`${property}@Enum`] = currentProperty.options.map((obj) => {
                    const key = keys(obj)[0];
                    return isNaN(key) || key.trim() === "" ? key : Number(key);
                });
                this._jtype[section][`${property}@Display.enum`] = currentProperty.options.map(
                    (obj) => values(obj)[0]
                );
            } else if (currentProperty.dataType === "jType") {
                Object.assign(this._jtype[section], currentProperty.value || {});
            }

            if (currentProperty.dataType === "dateTime") {
                this._jtype[section][property] = this._toLocaleString(currentProperty.value);
            } else {
                if (currentProperty.dataType !== "jType") {
                    this._jtype[section][property] = currentProperty.value;
                }
            }

            // Additional Properties
            if (currentProperty.readOnly) {
                this._jtype[section][`${property}@Display.readOnly`] = true;
            }
            if (currentProperty.required) {
                this._jtype[section][`${property}@Required`] = true;
            }
        });

        if (!isEmpty(sectionObject.jtype)) {
            this._jtype[`${section}@Display.label`] = sectionObject.label;
            this._jtype[section] = sectionObject.jtype;
        }
        if (!isEmpty(sectionObject.typeDef)) {
            Object.assign(this._jtype, sectionObject.typeDef);
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFormsPendingPatchesChanged() {
        this._model.emitFormsPendingPatchesChanged();
    }

    _setFormPristineValue(pristineValue) {
        this._model._pristineValue = pristineValue;
    }

    _setEntitySchemaObject(entitySchemaObject) {
        this._entitySchema = entitySchemaObject;
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _toLocaleString(val) {
        let d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    _setHeightToFloatingDiv(height) {
        this._floatingNavigation.style.height = `${height - 35}px`;
        this._floatingNavigation.style.maxHeight = `${this._maxHeight}px`;
    }

    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}
module.exports = ExpandCollapseRenderer;
